import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetUserQuery } from "./../slices/userApiSlice";
import { login, setEnableSearchHyperlinks } from "./../slices/authSlice";
import { setFlag } from "./../slices/appSlice";
import { useDispatch, useSelector } from "react-redux";
import cookieService from "./../services/CookieService";
import { useLocation, useNavigate } from "react-router-dom";
import config from "./../config";
import { setReportEnabledOrgs, setUserDetails } from "./../slices/userSlice";

import {
    addLastSelectedOrganization,
    addSelectedOrganization,
    clearSelectedOrganizations,
    setTotalParentOrganization,
    setOrganizationsCount,
    setSelectedOrgainzationsId,
} from "./../slices/organizationSlice";
import UserRepository from "./../repositories/UserRepository";
import apiSlice from "./../slices/apiSlice";
import {
    addSelectedOrganizationForReports,
    clearSelectedOrganizationsForReports,
    clearPortalUserData,
    setSelectedOrgainzationsIdForReports,
    setSelectedRelatedOrganizationsForReports,
} from "./../slices/viewReportSlice";
import {
    setLoginMessage,
    setUserPreference,
    setPreferenceDataFetched,
    setReleaseMessage,
} from "./../slices/preferenceSlice";
import { useLazyGetUserPreferenceDataQuery } from "./../slices/userPreferenceApiSlice";
import OrganizationRepository from "./../repositories/OrganizationRepository";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
    setDefinitionsExplained,
    setFrequentlyAskedQuestions,
    setHelpContentSection,
} from "./../slices/helpContentSlice";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { clearAllCache } from "./../utils/indexedDBCache";
import useNetworkStatus from "./../hooks/useNetworkStatus";
import { useLazyGetODSPortalUserPermissionsQuery } from "./../slices/OdsApiSlice";
import {
    evaluateOdsAccess,
    getUserTypeFromDetails,
} from "./../utils/featureFlagUtils";
import {
    useLazyGetEmployeeClaimDetailsQuery,
    useLazyGetEmployeeLeaveDetailsQuery,
} from "../slices/employeesApiSlice";
import { noDataPlaceholder } from "../constants";

const useAuth = (
    { isDeepLinked, isAuthenticated } = {
        isDeepLinked: false,
        isAuthenticated: false,
    }
) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const {
        displayReports,
        enableConsole,
        enableSearchHyperlink,
        enabledOdsForSearch,
    } = useFlags();

    const { isOffline } = useNetworkStatus({ enableLogging: enableConsole });
    const flagsLoaded = enableConsole !== undefined;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const [
        getEmployeeLeaveDetails,
        {
            data: leaveDetails = null,
            isError: isLeaveApiError,
            isFetching: isLeaveFetching,
        },
    ] = useLazyGetEmployeeLeaveDetailsQuery();
    const [
        getEmployeeClaimDetails,
        {
            data: claimDetails = null,
            isError: isClaimApiError,
            isFetching: isClaimFetching,
        },
    ] = useLazyGetEmployeeClaimDetailsQuery();

    const { reportEnabledOrganizations } = useSelector(
        (state) => state.userDetails
    );

    const {
        data: users = null,
        error,
        startedTimeStamp: permissionsStartTime,
        fulfilledTimeStamp: permissionsEndTime,
        status: permissionsStatus,
    } = useGetUserQuery(
        flagsLoaded
            ? {
                  customVariables: {
                      enableConsole: flagsLoaded,
                  },
              }
            : skipToken,
        {
            skip:
                isAuthenticated ||
                !flagsLoaded ||
                !evaluateOdsAccess(enabledOdsForSearch).shouldCallMuleSoft,
        }
    );

    const [
        getODSPortalUserPermissions,
        {
            data: odsPermissionsData = null,
            status: odsPermissionsStatus,
            startedTimeStamp: odsPermissionsStartedTime,
            fulfilledTimeStamp: odsPermissionsEndTime,
            error: odsPermissionsError,
        },
    ] = useLazyGetODSPortalUserPermissionsQuery();

    const { user, deepLinkUrl, dlselectedIsfOrganizationId } = useSelector(
        (state) => state.auth
    );

    const { organizationPreference } = useSelector(
        (state) => state.preferenceSlice
    );
    const { organizations: allOrganizations } = useSelector(
        (state) => state.userDetails
    );

    const [
        getUserPreferenceData,
        {
            data: preferenceData,
            isSuccess: preferenceDataLoaded,
            startedTimeStamp: preferenceStartedTimeStamp,
            fulfilledTimeStamp: preferenceFulfilledTimeStamp,
            status: preferenceStatus,
            error: preferenceError,
        },
    ] = useLazyGetUserPreferenceDataQuery();

    // Guards against duplicate ODS permission calls across re-renders/effect re-runs.
    const hasRequestedOdsPermissionsRef = useRef(false);
    const hasLoggedPermissionsErrorRef = useRef(false);
    const hasLoggedOdsPermissionsErrorRef = useRef(false);
    const hasLoggedPreferenceErrorRef = useRef(false);
    const defaultOdsDecision = useMemo(
        () => evaluateOdsAccess(enabledOdsForSearch),
        [enabledOdsForSearch]
    );
    const userBasedOdsDecision = useMemo(
        () => evaluateOdsAccess(enabledOdsForSearch, users?.userDetails),
        [enabledOdsForSearch, users]
    );

    const shouldCallMuleSoft = defaultOdsDecision.shouldCallMuleSoft;
    const directMode = !shouldCallMuleSoft && defaultOdsDecision.shouldCallODS;
    const shouldCallODS = directMode
        ? defaultOdsDecision.shouldCallODS
        : userBasedOdsDecision.shouldCallODS;
    const hasPermissionApiFailure =
        permissionsStatus === "rejected" || odsPermissionsStatus === "rejected";
    const hasInitializedUserSessionRef = useRef(false);

    const isApiError = Boolean(error) || hasPermissionApiFailure;

    const muleSoftApiCompleted =
        !shouldCallMuleSoft ||
        permissionsStatus === "fulfilled" ||
        permissionsStatus === "rejected";
    const odsApiCompleted =
        !shouldCallODS ||
        odsPermissionsStatus === "fulfilled" ||
        odsPermissionsStatus === "rejected";
    const shouldCallPreferenceApi =
        (muleSoftApiCompleted || odsApiCompleted) && !hasPermissionApiFailure;
    const preferenceApiCompleted =
        !shouldCallPreferenceApi ||
        preferenceStatus === "fulfilled" ||
        preferenceStatus === "rejected";
    const allExpectedApisCompleted =
        flagsLoaded &&
        muleSoftApiCompleted &&
        odsApiCompleted &&
        preferenceApiCompleted;
    const shouldShowProgressBar =
        !isOffline &&
        (!allExpectedApisCompleted || isLeaveFetching || isClaimFetching);

    useEffect(() => {
        if (isAuthenticated) return;

        if (permissionsStatus === "fulfilled" && enableConsole) {
            console.log(
                `permissions API executed successfully | Start: ${new Date(
                    permissionsStartTime
                ).toLocaleString()} | End: ${new Date(
                    permissionsEndTime
                ).toLocaleString()}`
            );
        }
    }, [
        permissionsStatus,
        enableConsole,
        permissionsStartTime,
        permissionsEndTime,
        isAuthenticated,
    ]);

    useEffect(() => {
        if (isAuthenticated) return;

        if (permissionsStatus !== "rejected") {
            hasLoggedPermissionsErrorRef.current = false;
            return;
        }

        if (!enableConsole || hasLoggedPermissionsErrorRef.current) {
            return;
        }

        hasLoggedPermissionsErrorRef.current = true;
        console.error("permissions API failed", error);
    }, [enableConsole, permissionsStatus, error, isAuthenticated]);

    useEffect(() => {
        if (!enableConsole || isAuthenticated) return;

        if (
            odsPermissionsStatus === "fulfilled" &&
            odsPermissionsStartedTime &&
            odsPermissionsEndTime
        ) {
            console.log(
                `ODS permissions API executed successfully | Start: ${new Date(
                    odsPermissionsStartedTime
                ).toLocaleString()} | End: ${new Date(
                    odsPermissionsEndTime
                ).toLocaleString()}`
            );
        }

        if (odsPermissionsStatus !== "rejected") {
            hasLoggedOdsPermissionsErrorRef.current = false;
            return;
        }

        if (hasLoggedOdsPermissionsErrorRef.current) {
            return;
        }

        hasLoggedOdsPermissionsErrorRef.current = true;
        console.error("ODS permissions API failed", odsPermissionsError);
    }, [
        enableConsole,
        odsPermissionsStatus,
        odsPermissionsStartedTime,
        odsPermissionsEndTime,
        odsPermissionsError,
        isAuthenticated,
    ]);

    useEffect(() => {
        if (!flagsLoaded || isAuthenticated) {
            return;
        }

        const userDetails = users?.userDetails;

        if (!enableConsole) {
            console.log("ODS decision", {
                shouldCallMuleSoft,
                shouldCallODS,
                directMode,
                userType: userDetails
                    ? getUserTypeFromDetails(userDetails)
                    : "unknown",
            });
        }

        dispatch(setFlag({ flag: "isODSEnabled", value: shouldCallODS }));

        if (!shouldCallODS || hasRequestedOdsPermissionsRef.current) {
            return;
        }

        hasRequestedOdsPermissionsRef.current = true;
        getODSPortalUserPermissions();
    }, [
        flagsLoaded,
        users,
        enabledOdsForSearch,
        directMode,
        shouldCallODS,
        shouldCallMuleSoft,
        enableConsole,
        dispatch,
        getODSPortalUserPermissions,
        isAuthenticated,
    ]);

    useEffect(() => {
        if (!enableConsole || isAuthenticated) return;

        const endTime = preferenceFulfilledTimeStamp
            ? new Date(preferenceFulfilledTimeStamp).toLocaleString()
            : "N/A";
        const startTime = new Date(preferenceStartedTimeStamp).toLocaleString();
        let statusMessage = null;

        if (preferenceStatus === "fulfilled") {
            hasLoggedPreferenceErrorRef.current = false;
            statusMessage = "executed successfully";
        } else if (preferenceStatus === "rejected") {
            statusMessage = "failed";
        }

        if (statusMessage) {
            if (preferenceStatus === "rejected") {
                if (hasLoggedPreferenceErrorRef.current) {
                    return;
                }

                hasLoggedPreferenceErrorRef.current = true;
                console.error(
                    `user-data/preference API ${statusMessage} | Start: ${startTime} | End: ${endTime}`,
                    preferenceError
                );
                return;
            }

            console.log(
                `user-data/preference API ${statusMessage} | Start: ${startTime} | End: ${endTime}`
            );
        }
    }, [
        enableConsole,
        preferenceFulfilledTimeStamp,
        preferenceStartedTimeStamp,
        preferenceStatus,
        preferenceError,
        isAuthenticated,
    ]);

    // Helper function to check if all organizations have the same group ID
    const checkOrganizationGroupUniformity = (organizations) => {
        if (organizations.length === 0) {
            return { isAllSame: true, relatedOrganizations: [] };
        }

        const firstOrganizationGroupId = organizations[0].ORGANIZATIONGROUPID;
        const relatedOrganizations = [];
        let isAllSame = true;

        for (const item of organizations) {
            const organizationGroupId = item.ORGANIZATIONGROUPID;

            if (
                organizationGroupId === null ||
                organizationGroupId !== firstOrganizationGroupId
            ) {
                isAllSame = false;
                break;
            }

            relatedOrganizations.push(item);
        }

        return { isAllSame, relatedOrganizations };
    };

    // Helper function to handle single organization selection
    const handleSingleOrganizationSelection = (
        dispatch,
        organizations,
        relatedOrganizations,
        checkOrganizationLength,
        organizationPreference
    ) => {
        const selectedOrganization = [...organizations].sort((a, b) =>
            a.ORGNIZATIONNAME.localeCompare(b.ORGNIZATIONNAME)
        );

        const orgData = {
            id: selectedOrganization[0].ORGANIZATIONID,
            organization: selectedOrganization[0],
            related: relatedOrganizations,
        };

        dispatch(addSelectedOrganization(orgData));
        dispatch(
            setSelectedOrgainzationsId(selectedOrganization[0].ORGANIZATIONID)
        );
        dispatch(addSelectedOrganizationForReports(orgData));
        dispatch(
            setSelectedOrgainzationsIdForReports(
                selectedOrganization[0].ORGANIZATIONID
            )
        );
        dispatch(addLastSelectedOrganization(selectedOrganization[0]));
        dispatch(setOrganizationsCount(checkOrganizationLength));
        dispatch(setTotalParentOrganization(1));

        // Pre-check whichever related orgs were saved in the preference. With no preference,
        // leave the related-org selection empty so the user picks from the dropdown.
        if (relatedOrganizations.length > 0) {
            const selected =
                organizationPreference?.length > 0
                    ? buildSelectedOptions(
                          relatedOrganizations,
                          organizationPreference
                      )
                    : [];

            dispatch(setSelectedRelatedOrganizationsForReports(selected));
        }
    };

    // Helper function to handle preference-based organization selection
    const handlePreferenceOrganizationSelection = useCallback(
        (
            dispatch,
            allOrganizations,
            organizationPreference,
            organizations,
            isAllSame
        ) => {
            const preferenceStoredOrg =
                OrganizationRepository.getRelatedOrganizationsFromIds(
                    allOrganizations,
                    organizationPreference
                );

            if (
                organizationPreference.length === 0 ||
                !preferenceStoredOrg[0]
            ) {
                return;
            }

            const relatedOrgs =
                preferenceStoredOrg.length > 1 ? preferenceStoredOrg : [];

            dispatch(
                addSelectedOrganization({
                    id: preferenceStoredOrg[0]?.ORGANIZATIONID,
                    organization: preferenceStoredOrg[0],
                    related: relatedOrgs,
                })
            );

            dispatch(addLastSelectedOrganization(preferenceStoredOrg[0]));
            dispatch(setTotalParentOrganization(organizations.length));
            dispatch(setOrganizationsCount(isAllSame));

            const reportsRelated =
                preferenceStoredOrg?.length > 0 &&
                preferenceStoredOrg[0]?.ORGANIZATIONGROUPID !== null
                    ? preferenceStoredOrg
                    : [];
            dispatch(
                addSelectedOrganizationForReports({
                    id: preferenceStoredOrg[0]?.ORGANIZATIONID,
                    organization: preferenceStoredOrg[0],
                    related: reportsRelated,
                })
            );

            dispatch(
                setSelectedOrgainzationsIdForReports(
                    preferenceStoredOrg[0]?.ORGANIZATIONID
                )
            );

            const selected = buildSelectedOptions(
                preferenceStoredOrg,
                organizationPreference
            );
            dispatch(setSelectedRelatedOrganizationsForReports(selected));
        },
        []
    );

    // Helper function to build selected options
    const buildSelectedOptions = (
        preferenceStoredOrg,
        organizationPreference
    ) => {
        const selected = [];

        Object.values(preferenceStoredOrg).forEach((item) => {
            if (
                organizationPreference.includes(
                    item.ORGANIZATIONIDISF.toString()
                )
            ) {
                selected.push({
                    value: item.ORGANIZATIONIDISF,
                    label: item.ORGNIZATIONNAME,
                    organizationIDISF: item.ORGANIZATIONIDISF,
                    organizationGroupId: item.ORGANIZATIONGROUPID,
                });
            }
        });

        return selected;
    };

    // Helper function to initialize user data
    const initializeUserData = (dispatch, users) => {
        const { userDetails, policies } =
            UserRepository.formatUserResponse(users);
        const organizations =
            UserRepository.formatUserResponse(users).organizations;
        const cookieToken = cookieService.get(config.REACT_APP_SSO_COOKIE_NAME);

        dispatch(clearSelectedOrganizations());
        dispatch(clearSelectedOrganizationsForReports());
        dispatch(apiSlice.util.resetApiState());
        dispatch(clearPortalUserData());
        dispatch(login({ user: userDetails, token: cookieToken }));
        dispatch(setUserDetails({ organizations, policies }));

        return { organizations, userDetails };
    };

    const initializeOdsUserData = useCallback(
        (dispatch, formattedOdsUserData) => {
            const { organizations, policies, userDetails } =
                formattedOdsUserData;
            const cookieToken = cookieService.get(
                config.REACT_APP_SSO_COOKIE_NAME
            );

            dispatch(clearSelectedOrganizations());
            dispatch(clearSelectedOrganizationsForReports());
            dispatch(apiSlice.util.resetApiState());
            dispatch(clearPortalUserData());
            dispatch(login({ user: userDetails, token: cookieToken }));
            dispatch(setUserDetails({ organizations, policies }));

            return { organizations, userDetails };
        },
        []
    );

    // Helper function to check if preference data is valid
    const hasValidPreferenceData = (
        preferenceData,
        preferenceDataLoaded,
        organizationPreference
    ) => {
        return (
            (preferenceData !== null || preferenceDataLoaded !== null) &&
            organizationPreference?.length > 0 &&
            organizationPreference?.toString() !== ""
        );
    };

    useEffect(() => {
        if (isAuthenticated) return;

        let organizations = [];
        let userDetails = null;

        if (shouldCallODS) {
            if (!odsPermissionsData) {
                return;
            }

            organizations = odsPermissionsData.organizations;
            userDetails = odsPermissionsData.userDetails;

            if (!hasInitializedUserSessionRef.current) {
                initializeOdsUserData(dispatch, odsPermissionsData);
                hasInitializedUserSessionRef.current = true;
            }
        } else {
            if (users === null) {
                return;
            }

            const formattedUserResponse =
                UserRepository.formatUserResponse(users);
            organizations = formattedUserResponse.organizations;
            userDetails = formattedUserResponse.userDetails;
            if (!hasInitializedUserSessionRef.current) {
                initializeUserData(dispatch, users);
                hasInitializedUserSessionRef.current = true;
            }
        }

        // Evaluate enableSearchHyperlink flag and freeze for session
        if (userDetails && enableSearchHyperlink) {
            const isUserMatched = enableSearchHyperlink.isfUserIds?.includes(
                userDetails.userId.toLowerCase()
            );
            const internalCondition =
                userDetails.isUserInternal === true &&
                enableSearchHyperlink.enableInternalUser === true;
            const externalCondition =
                userDetails.isUserInternal === false &&
                enableSearchHyperlink.enableExternalUser === true;
            dispatch(
                setEnableSearchHyperlinks(
                    isUserMatched || internalCondition || externalCondition
                )
            );
        } else {
            dispatch(setEnableSearchHyperlinks(false));
        }

        const { isAllSame, relatedOrganizations } =
            checkOrganizationGroupUniformity(organizations);
        const checkOrganizationLength =
            organizations.length === 1 ? "true" : "false";

        if (isAllSame || organizations.length === 1) {
            handleSingleOrganizationSelection(
                dispatch,
                organizations,
                relatedOrganizations,
                checkOrganizationLength,
                organizationPreference
            );
        } else if (
            hasValidPreferenceData(
                preferenceData,
                preferenceDataLoaded,
                organizationPreference
            )
        ) {
            handlePreferenceOrganizationSelection(
                dispatch,
                organizations,
                organizationPreference,
                organizations,
                isAllSame
            );
        } else {
            dispatch(setTotalParentOrganization(organizations.length));
            dispatch(setOrganizationsCount(isAllSame));
        }
    }, [
        shouldCallODS,
        odsPermissionsData,
        initializeOdsUserData,
        users,
        preferenceDataLoaded,
        dispatch,
        preferenceData,
        organizationPreference,
        enableSearchHyperlink,
        handlePreferenceOrganizationSelection,
        isAuthenticated,
    ]);

    useEffect(() => {
        if (!user?.userId || hasPermissionApiFailure || isAuthenticated) {
            return;
        }

        getUserPreferenceData({ userId: user.userId });
    }, [
        user?.userId,
        user?.userEmail,
        hasPermissionApiFailure,
        getUserPreferenceData,
        isAuthenticated,
    ]);

    useEffect(() => {
        if (isAuthenticated) return;

        if (
            reportEnabledOrganizations !== null &&
            (reportEnabledOrganizations?.enabledOrganizationsDisabilitySnapshot
                ?.length > 0 ||
                reportEnabledOrganizations
                    ?.enabledOrganizationsTrendsBenchmarking?.length > 0)
        ) {
            dispatch(
                setReportEnabledOrgs({
                    enabledOrganizationsDisabilitySnapshot:
                        reportEnabledOrganizations.enabledOrganizationsDisabilitySnapshot,
                    enabledOrganizationsTrendsBenchmarking:
                        reportEnabledOrganizations.enabledOrganizationsTrendsBenchmarking,
                })
            );
        }
    }, [reportEnabledOrganizations, dispatch, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) return;

        if (user && preferenceDataLoaded) {
            const preference = preferenceData?.UserPreferences;
            const loginMessage = preferenceData?.LoginMessage;
            const releaseMessage =
                preferenceData?.SitecoreResponse?.ReleaseMessage;
            const definitionsExplained =
                preferenceData?.SitecoreResponse?.DefinitionsExplained;
            const helpContentSection =
                preferenceData?.SitecoreResponse?.HelpSection;
            const frequentlyAskedQuestions =
                preferenceData?.SitecoreResponse?.FAQ;

            const homePreference = preference?.HomePreference ?? [""];

            dispatch(
                setUserPreference({
                    homePreference,
                    organizationPreference: preference?.OrganizationPreference,
                    userSelectedOrganizationPreference:
                        preference?.OrganizationPreference,
                    searchPreference: preference?.SearchPreference,
                })
            );

            dispatch(
                setReleaseMessage({
                    releaseHeader: releaseMessage?.Header,
                    releaseLink: releaseMessage?.ReleaseLink,
                    releaseContent: releaseMessage?.Content,
                })
            );

            dispatch(setDefinitionsExplained(definitionsExplained));
            dispatch(setHelpContentSection(helpContentSection));
            dispatch(setFrequentlyAskedQuestions(frequentlyAskedQuestions));
            dispatch(setPreferenceDataFetched(true));
            dispatch(setLoginMessage({ loginMessage }));
        }
    }, [user, preferenceDataLoaded, preferenceData, dispatch, isAuthenticated]);

    useEffect(() => {
        if (!leaveDetails) return;

        if (
            Array.isArray(leaveDetails.Header) &&
            leaveDetails.Header.length > 0
        ) {
            const header = leaveDetails.Header[0];
            if (header.OrganizationId) {
                if (header.EmployeeId && header.EmployeeId !== "Not Provided") {
                    navigate(
                        `/organization/${header.OrganizationId.toLowerCase()}/employees/${header.EmployeeId}?leave=${header.LeaveNo}`
                    );
                } else {
                    navigate(
                        `/organization/${header.OrganizationId.toLowerCase()}/employees/view?leave=${header.LeaveNo}`
                    );
                }
                return;
            }
        }

        // Handle Error - No Records Found
        navigate(`/no-records`, {
            state: {
                show: true,
            },
        });
    }, [leaveDetails, navigate]);

    useEffect(() => {
        if (!claimDetails) return;

        const header = claimDetails.Header;

        if (header && header.OrganizationId) {
            if (
                header.EmployeeID &&
                header.EmployeeID !== noDataPlaceholder &&
                header.EmployeeID !== "Not Provided"
            ) {
                navigate(
                    `/organization/${header.OrganizationId.toLowerCase()}/employees/${header.EmployeeID}?claim=${header.ClaimNumber}`
                );
            } else {
                navigate(
                    `/organization/${header.OrganizationId.toLowerCase()}/employees/view?claim=${header.ClaimNumber}`
                );
            }
            return;
        }

        // Handle Error - No Records Found
        navigate(`/no-records`, {
            state: {
                show: true,
            },
        });
    }, [claimDetails, navigate]);

    useEffect(() => {
        if (isLeaveApiError || isClaimApiError) {
            navigate("/error", {
                state: {
                    show: true,
                },
            });
        }
    }, [isLeaveApiError, isClaimApiError, navigate]);

    useEffect(() => {
        if (!user || isAuthenticated) return;

        // Check if all 3 APIs have completed (either successfully or with error)
        const userApiCompleted =
            !shouldCallMuleSoft ||
            permissionsStatus === "fulfilled" ||
            permissionsStatus === "rejected";

        const preferenceApiCompleted =
            preferenceStatus === "fulfilled" || preferenceStatus === "rejected";

        setIsLoggedIn(true);
        if (
            !userApiCompleted ||
            !preferenceApiCompleted ||
            isOffline ||
            isDeepLinked
        ) {
            return;
        }

        if (preferenceError) {
            console.info("Preference Api failed, redirecting to search");
            navigate("/search");
            return;
        }

        const { leave: leaveNo, claim: claimNo } = location.state?.params || {};

        if (leaveNo) {
            getEmployeeLeaveDetails({
                leaveNo,
            });
        } else if (claimNo) {
            getEmployeeClaimDetails({
                claimNo,
            });
        } else {
            if (!preferenceDataLoaded) return;

            const preference = preferenceData?.UserPreferences;
            const homePreference = preference?.HomePreference ?? [""];

            const hasDeepLink =
                deepLinkUrl === "rc" ||
                deepLinkUrl === "report" ||
                dlselectedIsfOrganizationId.length > 0;

            if (hasDeepLink) {
                navigate("/reportingcapabilities");
            } else if (displayReports) {
                navigate(
                    `/${
                        homePreference.length > 0
                            ? homePreference.toString()
                            : "home"
                    }`
                );
            } else {
                navigate("/search");
            }
        }
    }, [
        user,
        preferenceDataLoaded,
        preferenceData,
        deepLinkUrl,
        dlselectedIsfOrganizationId,
        allOrganizations,
        preferenceError,
        permissionsStatus,
        preferenceStatus,
        displayReports,
        isOffline,
        shouldCallMuleSoft,
        navigate,
        location.state,
        getEmployeeLeaveDetails,
        getEmployeeClaimDetails,
        isAuthenticated,
        isDeepLinked,
    ]);

    useEffect(() => {
        if (isAuthenticated) return;

        clearAllCache();
    }, [isAuthenticated]);

    return { shouldShowProgressBar, isApiError, isLoggedIn };
};

export default useAuth;
